import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { useGetTedLessonPlanner } from "@packages/api/madlenAPI";
import TedPlanDetails from "./TedPlanDetails";

const TedLessonPlan = () => {
  const { planId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: plan,
    isLoading,
    error,
  } = useGetTedLessonPlanner(parseInt(planId || "0"), {
    query: {
      enabled: !!planId && !isNaN(parseInt(planId)),
    },
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
        <div className="container max-w-6xl mx-auto px-4 py-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center min-h-[400px] space-y-4"
          >
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-gray-600 dark:text-gray-400">
              {t("tedPlan.loading")}
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !plan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
        <div className="container max-w-6xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate("/lesson-planning-tools/ted-plan")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center min-h-[400px] space-y-4 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-sm p-8 border border-gray-100 dark:border-gray-700"
          >
            <AlertCircle className="w-12 h-12 text-red-500" />
            <div className="text-center space-y-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t("tedPlan.error.title")}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {error?.message || t("tedPlan.error.notFound")}
              </p>
            </div>
            <button
              onClick={() => navigate("/lesson-planning-tools/ted-plan")}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              {t("tedPlan.backToPlans")}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Success state - render plan details
  return <TedPlanDetails plan={plan as any} />;
};

export default TedLessonPlan;
